// What the caller IS and which product modules the tenant holds, read from the
// one place the API says it.
//
// # The screens were guessing, and the guess was wrong for almost everybody
//
// `/v1/session` returned scopes and no roles, so the shell asked the only question it
// could:
//
//     const administering = state.riksu.scopes.includes("riksu.write");
//
// `riksu.write` is what an ordinary employee's token carries — it is what lets them
// request their own leave and open their own expense claim. So that expression was
// TRUE for essentially every signed-in person, and `/people` told all of them "you are
// seeing everybody in this company" while `riksu-authz` was quietly handing them
// themself and their reports. A screen that describes your access from a signal that
// does not answer the question tells most people something false about themselves.
//
// A scope answers *may you invoke this operation*. A role answers *are you HR*. They
// are different questions and only the second one narrows a list.
//
// # This is a COURTESY, never the enforcement
//
// The server narrows every read and refuses every write on its own, and it does not
// consult anything here. What this is for is not showing somebody a control that is
// going to be refused, and not describing their access wrongly — which is a different
// job from enforcing it, and a much less important one.
//
// A caller who edits their own session document reaches nothing new. That is the test
// of whether this file is doing anything dangerous, and it is not.

/**
 * The role names riksu gates on, mirrored from the backend's own constants.
 *
 * Duplicated rather than derived, because the contract carries no role vocabulary and
 * inventing one for this would be inventing an API. The mirror is small and the risk
 * of drift is real, which is why each name says where its original lives.
 */

import type { components } from "./api";

/** A closed licenseable product boundary, projected from the API contract. */
export type ProductModule = components["schemas"]["ProductModule"];

/** Shared infrastructure is explicit too; absence must never mean foundation. */
export type ModuleAccess =
  | { readonly kind: "foundation" }
  | { readonly kind: "licensed"; readonly module: ProductModule };
export const ROLES = {
  /** `people.rs` PEOPLE_ADMINISTRATORS — the personnel file and what it costs. */
  people: ["people.hr", "payroll.admin"],
  /** The original sealed certificate subset, independently of personnel payroll access. */
  professionalCertificates: ["people.hr"],
  /** Explicit corporate classification; only personnel administrators, no self-service. */
  personWorkContact: ["people.hr", "payroll.admin"],
  personnelNumber: ["people.hr"],
  /**
   * `people.rs` NAME_ADMINISTRATORS — NARROWER than `people` on purpose.
   *
   * Who may change what somebody is CALLED, which is the one correction riksu serves on
   * a person. `payroll.admin` is deliberately not here, on the argument
   * `ACCESS_ADMINISTRATORS` makes one section along in the same file: knowing what
   * somebody costs is not authority to change who they are.
   *
   * The server's rule admits **no relation at all** — not the person, not their manager
   * — which this mirror cannot express and does not need to: a role list answers "may
   * they reach the control", and hiding the pencil from everybody who lacks the role is
   * exactly right. The server keeps the finer question, the same split
   * `assets.manager` is written up under.
   */
  personName: ["people.hr"],
  /** Explicit employment-file purpose and evidence administration. */
  employeeFiles: ["people.hr"],
  /** `people/work_models.rs` WORK_MODEL_WRITERS. */
  employmentWorkModel: ["people.hr"],
  /**
   * `people.rs` IDENTITY_ADMINISTRATORS — who may restate a fact that IDENTIFIES somebody.
   *
   * THE SAME LIST AS `personName` TODAY AND ITS OWN ENTRY ANYWAY, which is `time`'s
   * argument and `documents`' argument for the third time: two constants that agree by
   * coincidence part company the day one of them moves, and `check-roles.mjs` is the only
   * thing that would notice. The server keeps them apart deliberately — a name rests on a
   * marriage certificate or a TMK md. 27 decision, a birth date and a T.C. kimlik numarası
   * rest on a nüfus record — so the day somebody widens who may rename a person, the
   * identity number must not follow.
   *
   * `only_roles` on the server, so neither the manager nor the person themself: a birth
   * date decides retirement eligibility, and a national identity number is an identity key
   * (migration 0024), which makes a self-service write a way to claim somebody else's
   * number and read the refusal.
   */
  personIdentity: ["people.hr"],
  /**
   * `people/corrections.rs` CONTACT_ADMINISTRATORS — who administers an e-mail or a phone.
   *
   * THE SAME LIST AS THE TWO ABOVE AND ITS OWN ENTRY, for their reason and for one more
   * that is specific to it: the server's requirement here is
   * `about_a_person_not_their_manager`, which admits **the person themself as well as the
   * role**, where a name and a birth date are `only_roles` and admit nobody. That
   * difference cannot be expressed in a role list at all, so the call site pairs this with
   * the reader's own person — `holdsAny(roles, ROLES.personContact) || id === mine` — and
   * merging this into `personIdentity` would silently offer self-service on a birth date
   * the day somebody reached for the shorter expression.
   *
   * The exclusion that matters is the same in both directions and is the server's: NOT the
   * line manager, more sharply than for a name, because that relation is transitive and
   * rewriting somebody's e-mail address is how you redirect what riksu sends them to
   * yourself. `payroll.admin` is out on `ACCESS_ADMINISTRATORS`' sentence.
   */
  personContact: ["people.hr"],
  /**
   * `people/access.rs` ACCESS_ADMINISTRATORS — who may give a person's record a way in.
   *
   * A `&str` rather than a `&[&str]` on the server, so `check-roles.mjs` does not read it
   * as a set; it is mirrored here anyway, because the alternative is a call site reaching
   * for `ROLES.people` and offering a payroll administrator a control riksu refuses. The
   * constant's own sentence is the argument: "knowing what somebody costs is not authority
   * to give them a way in."
   *
   * `only_role`, so no relation at all — not the person themself, since a relation would
   * let somebody grant themselves one.
   */
  personAccess: ["people.hr"],
  /** `leave.rs` LEAVE_ADMINISTRATORS. */
  leave: ["people.hr", "leave.admin"],
  /** `expense.rs` EXPENSE_ADMINISTRATORS. */
  expense: ["finance.admin", "people.hr"],
  /** `expense.rs` PAYMENT_READERS — finance's payment run, not the personnel file. */
  paymentReaders: ["finance.admin", "expense.finance"],
  /** `expense/reports.rs` EXPENSE_REPORT_READERS: aggregate purpose, never a payment writer. */
  expenseReportReaders: ["expense.reports_company", "expense.reports_department"],
  /**
   * `expense.rs` FINANCE — narrower than `expense` on purpose.
   *
   * Who MOVES money, as opposed to asking for it to move. Settling a claim is the write
   * that says the company paid and recording a hand-back is the write that says the
   * worker gave the money back — self-service on either is self-service on the balance
   * every reimbursement refusal is computed from. HR administers expenses and does not
   * hold the cash, so it is deliberately not in here.
   */
  finance: ["finance.admin"],
  /** `travel.rs` TRAVEL_ADMINISTRATORS — who may see beyond their reporting line. */
  travel: ["travel.admin", "people.hr"],
  /** `travel.rs` ITINERARY_ADMINISTRATORS — who records what was actually booked. */
  travelItinerary: ["travel.admin"],
  /** `onboarding.rs` JOIN_ADMINISTRATORS. */
  // Safety curriculum/evidence administration is HR only.
  candidateDocuments: ["people.hr"],
  safety: ["people.hr"],
  onboarding: ["people.hr", "onboarding.admin"],
  /** `offboarding.rs` EXIT_ADMINISTRATORS. */
  offboarding: ["people.hr", "offboarding.admin"],
  /**
   * `offboarding.rs` COMPLIANCE_READERS — WIDER than `offboarding`, and its own entry.
   *
   * Who reads the company's own regulatory exposure: the SGK deadline list, the breach
   * report, and `GET /v1/offboarding/work-certificates`, which is who is still owed a
   * 4857 md. 28 çalışma belgesi and who was given one. `compliance.officer` is in it and
   * is deliberately not in `offboarding`, because reading whether a duty was discharged
   * is not authority to change the record of it — the same split `time` and
   * `timeContractTerms` are written up under.
   *
   * A superset today, and mirrored separately anyway for the reason every other pair here
   * is: two constants that agree by containment part company the day one of them moves,
   * and `check-roles.mjs` is the only thing that would notice.
   */
  offboardingCompliance: ["people.hr", "offboarding.admin", "compliance.officer"],
  /** `organization.rs` STRUCTURE_ADMINISTRATORS. */
  organization: ["organization.admin", "people.hr"],
  /** people/workforce.rs WORKFORCE_READERS — aggregate workforce counts and departures only. */
  workforce: ["organization.hr_director", "organization.general_manager"],
  /** organization/reporting.rs REPORTING_CHANGE_AUTHORS — shared reviewed changes only. */
  reportingChange: ["organization.admin", "people.hr", "organization.hr_director"],
  /** `assets.rs` ASSET_ADMINISTRATORS — original inventory authoring only. */
  assets: ["assets.manager"],
  /** `assets.rs` ASSET_READERS — original personnel custody and record inspection. */
  assetsRead: ["assets.manager", "people.hr"],
  /** Original monthly warranty notice audience, assets/warranty_review.rs ROLE. */
  assetWarrantyReview: ["assets.manager"],
  /**
   * `time.rs` TIME_ADMINISTRATORS — who administers a timesheet.
   *
   * The SAME PAIR as `people` today and mirrored separately anyway, because they are two
   * concepts that happen to agree: HR keeps the record and payroll pays from it, which is
   * the sentence `time.rs` writes beside the constant. Deriving one from the other would
   * mean the day somebody widens attendance — a `time.admin`, a shift supervisor — the
   * screen silently follows the personnel file instead, or does not follow at all.
   *
   * Nothing on `/time` is gated on this, and that is the point of naming it: every one of
   * the four routes reaches `Themself` and `Manager` FIRST, so the screen is offered to
   * everybody and the role is what widens WHOSE timesheet riksu will answer about. The
   * mirror exists so `check-roles.mjs` catches a drift in the set, not so a control hides.
   */
  time: ["people.hr", "payroll.admin"],
  /**
   * `time.rs` CONTRACT_TERM_ADMINISTRATORS — who may move the line md. 41 divides pay at.
   *
   * NARROWER THAN `time`, and the omission is the whole control. Reading a timesheet is
   * administered by HR **and** payroll; writing the contracted week is HR alone, because
   * that figure is the threshold the two md. 41 instruments are drawn at — a payroll
   * administrator who could move it could change what a worker is owed for hours they have
   * already worked. `time.rs` says it in as many words: *"HR keeps the contract and payroll
   * pays from it."*
   *
   * It is the same separation `/payroll` draws in the OTHER direction, where `people.hr` is
   * deliberately not a payroll controller — so the two lists are not a hierarchy and
   * neither may be derived from the other.
   *
   * The server's rule is `Requirement::only_roles`, which admits **no relation at all** —
   * not the worker, not their manager. A worker setting their own contracted week sets the
   * line their own overtime is measured from. This mirror cannot express that and does not
   * need to: it answers "may they reach the control", and hiding it from everybody without
   * the role is exactly right. The finer question stays the server's, the split
   * `personName` is written up under.
   *
   * UNLIKE `time`, A CONTROL DOES HIDE ON THIS ONE. `/time` is offered to everybody and the
   * set-contracted-week action is drawn only for a holder — the shape `/advance` already
   * uses, where the one write taking `finance.admin` hides that row action rather than the
   * module.
   */
  contractTerm: ["people.hr"],
  /**
   * `payroll.rs` PAYROLL_CONTROLLERS — and `people.hr` is DELIBERATELY NOT IN IT.
   *
   * The one entry here that is narrower than the obvious guess, and the omission is the
   * whole control. Closing a month establishes the instant every later correction is
   * measured from, and the people who correct HR records are precisely the people that
   * measurement exists to observe — so HR administering payroll periods would be HR
   * marking its own homework.
   *
   * `payroll.admin` appears in `people` and in `time` above, which is a different claim
   * and not a contradiction: a payroll administrator may read a personnel file and a
   * timesheet. The overlap runs one way only, which is the shape of a separation of
   * duties rather than an oversight, and `check-roles.mjs` pins this set against the
   * backend's so nobody widens it by copying `people`'s.
   */
  payroll: ["payroll.admin", "finance.admin"],
  /**
   * `people/settlements.rs` — who may be quoted what a LEAVING costs, other than the
   * leaver themself.
   *
   * ITS OWN ENTRY AND NOT AN ALIAS OF `compensation`, on `time`'s argument for the fourth
   * time: the two lists agree today and are two different judgements, and this check is the
   * only thing that would notice the day one of them moves. The server does not name a
   * constant for it at all — `settlement` passes `&["payroll.admin"]` inline to
   * `Requirement::about_a_person_not_their_manager` — so this mirror names the handler
   * rather than a Rust identifier.
   *
   * **THE LINE MANAGER IS EXCLUDED, and that is the whole control.** It is a payslip's
   * rule stated on the one figure that is bigger than a payslip: a line manager decides
   * whether somebody leaves and has no business knowing what that leaving pays them.
   * `about_a_person_not_their_manager` is the server's spelling; a role list cannot express
   * *and also the person themself and NOT their manager*, so the screen composes it — the
   * reader's own linked person, or a holder of the role below, and nothing else.
   */
  settlement: ["payroll.admin"],
  /**
   * `compensation.rs` PAY_ADMINISTRATORS — who may read and restate employment pay.
   *
   * This is deliberately narrower than `payroll`. `finance.admin` may control a payroll
   * period without being entitled to a worker's compensation record, and `people.hr` may
   * administer the personnel file without being entitled to pay. Ordinary workers need no
   * role here: the server narrows the same read to their own employment.
   */
  compensation: ["payroll.admin"],
  /** `awards::AWARD_PROPOSAL_READERS` — proposal review, not recurring-pay access. */
  awardProposalReaders: ["payroll.admin", "finance.admin"],
  bonusProposalReaders: ["payroll.admin", "finance.admin"],
  /** `merit::MERIT_PROPOSAL_READERS` — review standing, not unrestricted pay access. */
  meritProposalReaders: ["payroll.admin", "finance.admin"],
  /** `performance.rs` PERFORMANCE_ADMINISTRATORS — the tenant-wide goal register. */
  performance: ["performance.admin"],
  /** reviews::calibration_sessions::CALIBRATION_PREPARERS. */
  calibrationPreparers: ["performance.admin", "performance.hr_director"],
  /** reviews::reports::COMPANY_REPORT_READERS; assigned reads retain their relationship. */
  performanceReportCompany: ["performance.hr_director", "performance.general_manager"],
  /** `mobility/mod.rs` MOBILITY_ADMINISTRATORS — opportunity ownership, never private matching. */
  mobility: ["mobility.admin"],
  learning: ["learning.admin"],
  /** `planning.rs` PLANNING_READERS — aggregate scenarios, never individual pay rows. */
  planning: ["planning.admin", "finance.admin"],
  /** `device_security/mod.rs` DEVICE_SECURITY_ADMINISTRATORS. */
  deviceSecurity: ["device.security_admin"],
  /**
   * `recruiting.rs` RECRUITERS — the CANDIDATE half: applications, offers, the headcount
   * and the awaiting-hire report.
   *
   * An application and an offer select a person's NAME, so this is the narrower of the
   * module's two lists and the separation is the point rather than an accident: being asked
   * to approve a rate is not a reason to be handed the pipeline.
   */
  recruiting: ["recruiting.admin", "people.hr"],
  recruitingLocations: ["recruiting.admin", "people.hr", "recruiting.hr_director"],
  requisitionReportReaders: ["recruiting.reports_company", "recruiting.reports_department"],
  /** Draft headcount authors; department scope is enforced by the server. */
  requisitionAuthors: ["recruiting.admin", "people.hr", "recruiting.department_manager"],
  /**
   * `recruiting.rs` EVALUATORS — scorecard authors, still narrowed by the server to an
   * application whose current interview names the caller. This grants no pipeline list
   * and no module destination; it only lets an inbox link open the assigned record.
   */
  recruitingEvaluators: [
    "recruiting.admin",
    "people.hr",
    "recruiting.hiring_manager",
    "recruiting.interviewer",
    "recruiting.department_manager",
  ],
  /**
   * `recruiting.rs` SEAT_READERS — the SEAT half: requisitions and the adverts against them.
   *
   * `recruiting` plus the two roles the requisition chain routes to. It exists because a
   * hiring manager and a finance approver hold **no recruiting endpoint at all** otherwise:
   * their entire surface is the inbox, so they are asked to authorise headcount and cannot
   * open the thing they are authorising.
   *
   * WHICH MAKES IT THE DESTINATION'S GATE rather than `recruiting`. Gating `/recruiting` on
   * the narrower list would undo that widening one layer up — the approver the server now
   * lets in would find no rail item, no palette entry and no link from the inbox row, which
   * is the defect `SEAT_READERS` was added to close, reintroduced in the browser. The two
   * SECTIONS that read the candidate half carry `recruiting` instead, so what a finance
   * approver is offered is the seat they are funding and nothing else.
   */
  recruitingSeats: [
    "recruiting.hr_director",
    "recruiting.admin",
    "people.hr",
    "recruiting.hiring_manager",
    "recruiting.finance",
    "recruiting.department_manager",
  ],
  /**
   * `recruiting/agencies.rs` AGREEMENT_KEEPERS — who registers a counterparty and records
   * the agreement with it.
   *
   * NEITHER OF THIS MODULE'S TWO LISTS, and that is the whole control rather than a third
   * spelling of one of them. Registering an agency and recording an agreement are
   * COMMERCIAL acts: the recruiter who receives the CVs recording the agreement that covers
   * them is the counterparty marking its own homework, which is the same separation riksu
   * already draws between `RECRUITERS` and `SEAT_READERS` pointed at a different axis.
   *
   * THE SAME PAIR AS `retention` TODAY AND ITS OWN ENTRY ANYWAY, which is the discipline
   * `time` and `documents` are both written up under and which the server states in as many
   * words beside its own constant: conducting a destruction review and recording a supplier
   * contract are different powers that happen to sit with the same holders. Two constants
   * that agree by coincidence part company the day one of them moves, and `check-roles.mjs`
   * is the only thing that would notice.
   */
  agencyRegister: ["compliance.auditor", "people.hr"],
  /**
   * `recruiting/agencies.rs` AGENCY_READERS — who may see WHICH agencies exist.
   *
   * WIDER THAN `agencyRegister`, and it is its own set rather than a widening: a recruiter
   * has to pick an agency when they record an application, and a list of company names is
   * not personal data. Nothing on that read says what was agreed with any of them beyond
   * whether a reference exists.
   *
   * IT IS THE ONLY ROLE LIST IN THIS FILE THAT CROSSES A MODULE'S OWN GATE, which is why
   * `/recruiting`'s destination requirement is the union of this and `recruitingSeats`:
   * `compliance.auditor` holds no recruiting endpoint at all otherwise, so gating the area
   * on the seat half alone would hide the register from the person it was built for — the
   * defect `recruitingSeats` itself exists to close, arriving one section along.
   */
  agencyReaders: ["compliance.auditor", "people.hr", "recruiting.admin"],
  /** `workflow.rs` PROCESS_ADMINISTRATORS — who changes certified approval routing. */
  workflow: ["organization.admin", "people.hr"],
  /** `content_reviews.rs` ADVERTISEMENT_POLICY_WRITERS. */
  advertisementContentPolicy: ["organization.admin", "people.hr"],
  /** `messaging.rs` MESSAGING_BROADCASTERS — who may address the active company. */
  messagingBroadcast: ["messaging.admin", "people.hr"],
  /**
   * `retention.rs` DESTRUCTION_ADMINISTRATORS — who conducts the periodic destruction
   * review, and it is **its own constant on the server** rather than an alias of the audit
   * roles.
   *
   * That distinction is the whole reason this entry exists as its own line. It is the same
   * two people who read the evidence log today, and the handler says why they are not one
   * set: reading what was destroyed and deciding to destroy somebody's personnel file are
   * different powers that happen to sit with the same holders. Aliasing them here would
   * move one the day somebody widened the other, silently and in the destroying direction —
   * which is `time`'s argument against deriving itself from `people`, on a surface where
   * the consequence is a record that no longer exists.
   *
   * `people.hr` beside `compliance.auditor` because a company small enough to have no DPO
   * still owes the duty, and a role nobody holds is a surface nobody can reach.
   */
  disclosureReaders: ["organization.admin", "compliance.auditor"],
  disclosureAdministrators: ["organization.admin"],
  /** notifications/templates.rs TEMPLATE_ADMINISTRATORS */
  notificationTemplates: ["organization.admin"],
  retention: ["compliance.auditor", "people.hr"],
  /** `routes.rs` AUDIT_ADMINISTRATORS; reading evidence grants no destruction authority. */
  auditReaders: ["compliance.auditor", "people.hr"],
  /**
   * `routes.rs` FILE_ADMINISTRATORS — who reads the whole tenant's document register.
   *
   * THE SAME PAIR AS `people` AND `time`, and mirrored separately for the reason `time`'s
   * entry gives at length: three constants that agree by coincidence part company the day
   * one of them moves, and this check is the only thing that would notice.
   *
   * WHAT IT GATES IS THE SCREEN AND NOT THE READ, which is the sharper half and is the
   * opposite of every other entry here. Neither chase list takes a role on the server —
   * both are `Access::Scope(scope::READ)` with `Subject::SpecialCategoryList` — and the
   * role is what `personal.visible_people` widens the answer with: without it a caller is
   * narrowed to `self_and_subordinates`, so they get their OWN unsigned contract rather
   * than a refusal. So this is not a mirror of a gate riksu enforces on those two routes;
   * it is the set for whom `/documents` is the tenant-wide chase list it exists to be.
   * Everybody else would meet a two-row report with no control on it, because
   * `POST /v1/documents/signatures` and the verification write are both headless.
   *
   * The pair is still pinned, because the SET is riksu's: the day somebody widens who
   * administers a personnel file, the screen has to follow.
   */
  documents: ["people.hr", "payroll.admin"],
  documentTemplates: ["people.hr", "organization.admin"],
  companyPolicies: ["people.hr"],
} as const;

/** Every role name riksu gates on anywhere, for the reconciliation check. */
export const EVERY_ROLE: readonly string[] = [...new Set(Object.values(ROLES).flat())].sort();

/**
 * The scopes riksu gates ROUTES on, mirrored from `riksu-api`'s own `scope` module.
 *
 * # A SCOPE IS NOT A ROLE, and they are two constants for that reason
 *
 * This file's whole header is the distinction: **a scope says what a CLIENT was granted;
 * a role says what a PERSON is.** They are different questions, riksu asks both, and a
 * single flat list of strings would make `riksu.audit.read` and `people.hr` interchangeable
 * to every reader of it — which is precisely the collapse that made the shell ask
 * `scopes.includes("riksu.write")` and tell an ordinary employee they administered the
 * company.
 *
 * # Only the ones a DESTINATION is gated on
 *
 * `READ` and `WRITE` are on every token riksu issues, so gating a screen on either would
 * be a condition that is always true — a check that cannot fail is a check nobody should
 * write. `PROVISION` belongs to the control plane and reaches no screen. That leaves the
 * one below, and it is the one that bit: `/retention` is the first destination in the
 * product whose routes need a scope beyond the universal two, and until this existed the
 * rail offered it to somebody riksu then refused with *"this route requires the
 * riksu.audit.read scope"*.
 *
 * `app/scope-label.ts` holds the same four wire values for a different purpose — turning
 * one into a sentence a person reads — and is deliberately not this: that map is keyed on
 * every scope a TOKEN may carry, including ones this bundle predates, while this names the
 * ones a SCREEN may be gated on.
 */
export const SCOPES = {
  /** `lib.rs` `scope::AUDIT_READ` — the evidence log and the administrative reads. */
  auditRead: "riksu.audit.read",
} as const;

/**
 * WHAT THE CALLER HOLDS, as one value carrying two different answers.
 *
 * One object rather than two positional arrays, and that is the whole safety of it: two
 * `readonly string[]` parameters side by side are swappable at a call site by a typo that
 * compiles, and the two things here must never be confused. `Session` satisfies this
 * structurally, so a call site passes `state.riksu` and nothing has to be assembled.
 *
 * All three members are mandatory because the session contract is mandatory. There is no
 * pre-customer compatibility shape to preserve: accepting a missing module set would turn
 * a malformed fixture or response into a mysteriously empty product instead of naming the
 * contract breach at compile time. The whole object may still be absent while sign-in is
 * unresolved, and the predicates fail closed for that state.
 */
export interface Held {
  readonly roles: readonly string[];
  readonly scopes: readonly string[];
  readonly modules: readonly ProductModule[];
}

/** Whether this tenant holds the named licensed product module. */
export function holdsModule(held: Held | undefined, wanted: ProductModule): boolean {
  return held?.modules.includes(wanted) ?? false;
}

/**
 * Whether the caller's token carries this scope.
 *
 * Deliberately NOT `holdsAny` with a different argument. The failure direction is the same
 * — absent means no — but writing it as its own function is what stops the two vocabularies
 * being passed to one predicate, which is the collapse `SCOPES`' own note argues against.
 */
export function holdsScope(held: Held | undefined, wanted: string): boolean {
  return held?.scopes.includes(wanted) ?? false;
}

/**
 * Whether the caller holds any of these roles.
 *
 * An EMPTY held list is an ordinary answer, not a loading state. Most people hold no
 * role at all, and a surface that waited for one would show an administrator's
 * affordances to an ordinary employee for as long as it waited.
 */
export function holdsAny(held: readonly string[] | undefined, wanted: readonly string[]): boolean {
  if (!held || held.length === 0) {
    return false;
  }
  return wanted.some((role) => held.includes(role));
}
